import Add from '@mui/icons-material/Add';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Remove from '@mui/icons-material/Remove';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import { expandIconStyles } from './styles';

export const ModeValues = {
  expandable: 'expandable',
  removable: 'removable',
  lookalikeEditable: 'lookalike-editable',
} as const;

export type ExpandableProps = {
  mode: typeof ModeValues.expandable;
  expanded: boolean;
  isSelected: boolean;
  onToggleExpand: () => void;
  onSelect: () => void;
};

export type RemovableProps = {
  mode: typeof ModeValues.removable;
  onRemove: () => void;
};

export type LookalikeEditableProps = {
  mode: typeof ModeValues.lookalikeEditable;
  onEdit: () => void;
  onRemove: () => void;
};

export type SegmentHeaderProps = ExpandableProps | RemovableProps | LookalikeEditableProps;

export type ExpandableElements = {
  leftIcon: ReactNode;
  rightIcon?: ReactNode;
  onClick: () => void;
};

export type RemovableElements = {
  leftIcon: ReactNode;
  rightIcon: ReactNode;
};

export type LookalikeEditableElements = {
  leftIcon: ReactNode;
  rightIcon: ReactNode;
};

export function useSegmentHeaderElements(props: ExpandableProps): ExpandableElements;
export function useSegmentHeaderElements(props: RemovableProps): RemovableElements;
export function useSegmentHeaderElements(props: LookalikeEditableProps): LookalikeEditableElements;
export function useSegmentHeaderElements(
  props: SegmentHeaderProps
): ExpandableElements | RemovableElements | LookalikeEditableElements {
  if (props.mode === 'expandable') {
    const { expanded, isSelected, onToggleExpand, onSelect } = props;

    const leftIcon = <ExpandMore color="primary" sx={expandIconStyles(expanded)} />;

    const rightIcon = !isSelected ? (
      <IconButton
        sx={{ alignSelf: 'center' }}
        className="icon-button"
        size="small"
        onClick={onSelect}
        data-test="add-segment-icon-button"
      >
        <Add />
      </IconButton>
    ) : null;

    return {
      leftIcon,
      rightIcon,
      onClick: onToggleExpand,
    };
  }

  if (props.mode === 'lookalike-editable') {
    return {
      leftIcon: <CheckCircleOutlined color="success" fontSize="small" />,
      rightIcon: (
        <Stack
          direction="row"
          sx={{
            gap: 0.5,
          }}
        >
          <IconButton
            sx={{ alignSelf: 'center' }}
            className="icon-button"
            size="small"
            onClick={props.onEdit}
            data-test="edit-segment-icon-button"
          >
            <Edit />
          </IconButton>

          <IconButton
            sx={{ alignSelf: 'center' }}
            className="icon-button"
            size="small"
            onClick={props.onRemove}
            data-test="remove-segment-icon-button"
          >
            <Remove />
          </IconButton>
        </Stack>
      ),
    };
  }

  return {
    leftIcon: <CheckCircleOutlined color="success" fontSize="small" />,
    rightIcon: (
      <IconButton
        sx={{ alignSelf: 'center' }}
        className="icon-button"
        size="small"
        onClick={props.onRemove}
        data-test="remove-segment-icon-button"
      >
        <Remove />
      </IconButton>
    ),
  };
}
